// src/pages/MergerDashboard/AllianceInstituteTab.js

import React, { useEffect, useState, useMemo } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { API_URL } from '../../utils/runtimeConfig';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';

const apiUrl = API_URL;
const barPalette = ['#2b4f72', '#4f7ea7', '#2f8f83', '#b2692b', '#8b4ea3', '#4f5b66', '#8b3a3a'];

const instituteKeys = [
  'searchAndRescue',
  'emergencyRoom',
  'allianceGather',
  'allianceBlitz',
  'mineReset',
  'nationalRecruitment',
  'fortifyAttack',
  'fortifyHP',
  'fortifyDefence',
  'allianceCannon',
  'shieldAll',
  'healthCare'
];

const instituteLabels = {
  searchAndRescue: 'Search and Rescue',
  emergencyRoom: 'Emergency Room',
  allianceGather: 'Alliance Gather',
  allianceBlitz: 'Alliance Blitz',
  mineReset: 'Mine Reset',
  nationalRecruitment: 'National Recruitment',
  fortifyAttack: 'Fortify Attack',
  fortifyHP: 'Fortify HP',
  fortifyDefence: 'Fortify Defense',
  allianceCannon: 'Alliance Cannon',
  shieldAll: 'Shield All',
  healthCare: 'Health Care'
};

const AllianceInstituteTab = ({ nationA, nationB }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [topAlliances, setTopAlliances] = useState([]);
  const [barData, setBarData] = useState([]);

  useEffect(() => {
    if (!nationA) return;

    const fetchAllianceInstitute = async () => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.get(`${apiUrl}/merger/alliance-institute`, {
          params: nationB ? { nationA, nationB } : { nationA }
        });

        setTopAlliances(response.data.topAlliances || []);
        setBarData(response.data.barData || []);
      } catch (error) {
        console.error('Error fetching alliance institute summary:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllianceInstitute();
  }, [nationA, nationB]);

  const groupedBarData = useMemo(() => {
    if (!barData.length) return [];

    return instituteKeys.map((key) => {
      const row = { skill: instituteLabels[key] || key };
      barData.forEach((alliance) => {
        row[alliance.acronym] = alliance[key] || 0;
      });
      return row;
    });
  }, [barData]);

  const getInstituteLevel = (alliance, skillKey) => {
    return alliance?.institute?.[skillKey]?.level || 0;
  };

  return (
    <div className="md-tech-tab">
      <h2>Alliance Institute Summary</h2>
      {isLoading && <p>Loading alliance institute data...</p>}

      {!isLoading && groupedBarData.length > 0 && (
        <div className="md-chart-wrap">
          <ResponsiveContainer>
            <BarChart data={groupedBarData} margin={{ top: 8, right: 20, left: 0, bottom: 48 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="skill" interval={0} angle={-25} textAnchor="end" height={86} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {barData.map((ally, index) => (
                <Bar
                  key={ally.acronym}
                  dataKey={ally.acronym}
                  name={ally.acronym}
                  fill={barPalette[index % barPalette.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {!isLoading && topAlliances.length > 0 && (
        <div className="md-table-wrap">
          <h3>Top 5 Alliances Detailed Table</h3>
          <table className="md-tech-summary-table">
            <thead>
              <tr>
                <th>Institute Skill</th>
                {topAlliances.map((ally) => (
                  <th key={ally._id}>{ally.acronym}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="md-tech-highlight-row">
                <td>Grand Total</td>
                {topAlliances.map((ally) => (
                  <td key={ally._id}>{ally.sums.grandTotal}</td>
                ))}
              </tr>

              {instituteKeys.map((skillKey) => (
                <tr key={skillKey}>
                  <td>{instituteLabels[skillKey] || skillKey}</td>
                  {topAlliances.map((ally) => (
                    <td key={ally._id}>{getInstituteLevel(ally, skillKey)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AllianceInstituteTab;
